package top.wcpe.allincore.ui.runtime.render

import kotlin.jvm.JvmField

/**
 * 渲染上下文：把"宿主平台才能提供的查询能力"传给 [Renderer]，避免
 * Renderer 直接耦合具体平台单例（fabric 的 ClientInventoryStore、SlotFeedbackController 等）。
 *
 * 由平台层在调用 [UIRenderer.render] 时构造实现；ui-runtime 测试用 [NONE]。
 *
 * ## RFC-RECOMPOSITION：evalContext 契约
 *
 * 当 UI 树包含 inline overlay（`ContentRef.Inline` 形态的 conditional tooltip / modal / menu 等）
 * 且其 selector / 子树表达式引用主屏 `state.x` / `slot.read(...)` 时，[evalContext] **必须** 返回
 * 主屏 [EvalContext]。
 * - 之前的兜底实现会构造空 EvalContext，让 selector 求值失败 → 容器子树清空 → 视觉上 tooltip
 *   完全消失，定位极困难；
 * - 当前实现仍然兜底，但 [OverlayContentFactory] 会在 inline 路径检测到 `evalContext == null`
 *   时输出 ERROR 日志（按 host id 节流），让真实漏传立刻可见。
 *
 * [NONE] 仅适用于无 inline overlay 的纯渲染单测路径；HUD / Screen 等真实平台实现必须实现
 * JVM 侧 `EvalAwareRenderContext.evalContext()`（IR 主体未迁 commonMain，evalContext 拆到 jvmMain 子接口）。
 */
interface RenderContext {

    /** 槽位物品视图查询；null 表示槽位为空。 */
    fun slotItem(groupId: String, key: String): SlotItemView?

    /** 槽位失败反馈查询；null 表示无反馈或已过期。 */
    fun slotFeedback(groupId: String, key: String): SlotFeedbackView?

    /** 是否 cursor 为空（hover 视觉仅在 cursor 空时显示）。 */
    fun cursorEmpty(): Boolean

    /**
     * 槽位是否在 vanilla quickCrafting 拖拽链中；非 null 表示该槽位将分到的 cursor 物品 +
     * 数量预览（用于半透明覆盖渲染）。该槽位本帧不再渲染 hover 高亮（vanilla 行为）。
     */
    fun quickCraftPreview(groupId: String, key: String): SlotItemView? = null

    /**
     * 按元素 id 查询其在屏幕坐标系下的包围盒（屏幕绝对坐标 + 尺寸）。
     *
     * 用途：modal 的 `anchorTarget` / `spotlightTarget` 在本 session root 树内通过 DFS 找不到目标时，
     * 回落到此方法跨 session（HUD ↔ Screen ↔ 其它 HUD）查询。由平台层（fabric）实现，通常查询全局
     * elementBox 快照（`lastHudElementBoxes` / `lastScreenElementBoxes`）。
     *
     * **语义约定**：返回值是**屏幕绝对坐标**（不含任何 root 偏移）；调用方拿到后直接当成屏幕坐标使用，
     * 不应再叠加 rootOffset。返回 null 表示该 id 不存在或对应元素当前不可见。
     *
     * @return `floatArrayOf(x, y, width, height)` 或 null
     */
    fun elementBox(elementId: String): FloatArray? = null

    /**
     * 鼠标光标屏幕 X（像素，GUI 缩放坐标系绝对坐标）。默认 [Float.NaN] 表示无鼠标（如 HUD / 纯单测），
     * 此时 FR-UI-031 粒子层的 hover 光标斥力关闭。由平台层（fabric Screen.render 的 mouseX）每帧提供。
     */
    fun mouseX(): Float = Float.NaN

    /** 鼠标光标屏幕 Y（像素）。约定同 [mouseX]。 */
    fun mouseY(): Float = Float.NaN

    /** 当前全局物品 Tooltip 是否应展示详细内容。 */
    fun tooltipDetailExpanded(): Boolean = false

    companion object {
        /** 占位/空实现，用于纯 ui-runtime 单测或无平台上下文的场景。 */
        @JvmField
        val NONE: RenderContext = object : RenderContext {
            override fun slotItem(groupId: String, key: String): SlotItemView? = null
            override fun slotFeedback(groupId: String, key: String): SlotFeedbackView? = null
            override fun cursorEmpty(): Boolean = true
        }
    }
}
